import { useState, useEffect, useTransition } from 'react';
import { Box, Chip, Skeleton, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { setMerchantId, setMerchant } from '../../../redux/pricingWizardSlice';
import { useLazyGetMerchantQuery, useGetServiceabilityGroupsQuery } from '../../../redux/commonApiCalls';
import PageTitle from '../../../components/PageTitle';
import WizardTopBar from './components/WizardTopBar';
import MerchantSearchBar from './components/MerchantSearchBar';
import WizardStepper from './components/WizardStepper';
import StageHeader from './components/StageHeader';
import StageFooter from './components/StageFooter';
import WizardDrawer from './components/WizardDrawer';
// File names retain their original numbering, but the v6 stage→component mapping
// is the HTML's 9-stage order (see switch below): Signup, KYC, Profile, Commercials,
// Rate Card, T&Cs, Review, Commit, Done.
import Stage0_Signup, { validateSignup } from './stages/Stage0_Signup';
import Stage1_Kyc from './stages/Stage1_Kyc';
import Stage2_Profile from './stages/Stage1_Profile';
import Stage3_Commercials, { validateAgreement } from './stages/Stage2_Agreement';
import Stage4_RateCard, { validateRateCard } from './stages/Stage4_RateCard';
import Stage5_Tcs, { validateTcs } from './stages/Stage5_Tcs';
import Stage6_Review, { validateReview } from './stages/Stage6_Review';
import Stage7_Commit from './stages/Stage7_Commit';
import Stage8_Done from './stages/Stage8_Done';
import { STAGES, MODE_ROWS, derivedServicesForMode, SERVICE_TYPES } from './constants';
import { nextStage, prevStage, setStage, setEditMode, setRateCardRows, recordConfirmation, updateWarehouse, updateSignup, updateMasterInfo, updateKycDoc, setKycCompanyType, setKycUserModified, toggleDirection, setPayment, toggleMode, setAgreement, setRvpEnabled, setRvpMode, updateCredit, setCodSplit, setInvoiceCeiling, updateBlendedTc, updateRto, setServiceabilityGroups, setServiceAccountCodes, setResolvedServices, setModeEnabled, setDirectionEnabled, updateRvpFormula, setRateCardMode, setArrangement, setEffectiveMode } from '../../../redux/pricingWizardSlice';
import { useCreateMerchantMutation, useUpdateMasterInfoOnlyMutation, useRegisterPickupMutation, useSubmitKycMutation, useUpdateBankDetailsMutation, useUpdateMerchantProfileMutation, useConfirmPricingCommercialsMutation, useSaveRateCardMutation, useSaveTncMutation, useCommitPricingScheduleMutation, useExecuteCommitMutation, useSaveDraftMutation, useLazyLoadMerchantPricingQuery } from '../../../redux/wizardApiSlice';
import { saveWizardState, loadWizardState, buildStageDraft } from './helpers/persist';
import { buildRateCardRows } from './helpers/rateEngine';


// Returns true if the user has entered ANY KYC data this session or in Redux
function hasAnyKycData(kyc, fileMap) {
  const { docs, companyType } = kyc;
  return !!(
    companyType ||
    docs.pan.number || docs.pan.file ||
    docs.gstin.number || docs.gstin.file ||
    docs.bank.account_no || docs.bank.file ||
    docs.signatory.file || docs.signatory.aadhaar_no || docs.signatory.name ||
    docs.msme.number || docs.msme.file ||
    Object.values(fileMap || {}).some(Boolean)
  );
}

// When any KYC data is entered, these 4 become mandatory
function mandatoryKycComplete(kyc, fileMap) {
  const { docs, companyType } = kyc;
  const hasBank = !!(fileMap?.bank || docs.bank.file || (docs.bank.account_no && docs.bank.ifsc));
  const hasGst  = !!(fileMap?.gstin || docs.gstin.file || docs.gstin.number);
  const hasSig  = !!(fileMap?.signatory || docs.signatory.file || docs.signatory.aadhaar_no);
  return !!companyType && hasBank && hasGst && hasSig;
}

export default function MerchantPricingWizard({ mode = 'onboard' }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const wizard = useSelector((s) => s.pricingWizard);
  const currentUser = useSelector((s) => s.user?.userInfo?.fullname || s.user?.userInfo?.name || s.user?.userInfo?.sub || 'Admin');
  const stage = wizard.stage;
  const { signup, agreement, resolvedServices: resolved, profile, merchantId } = wizard;

  const [isPending, startTransition] = useTransition();
  const [drawerKind, setDrawerKind] = useState(null);
  const [signupErrors, setSignupErrors] = useState({});
  const [fetchingMerchant, setFetchingMerchant] = useState(false);
  const [getMerchantById] = useLazyGetMerchantQuery();
  const [createMerchant, { isLoading: creatingMerchant }] = useCreateMerchantMutation();
  const [updateMasterInfoOnly] = useUpdateMasterInfoOnlyMutation();
  const [registerPickup] = useRegisterPickupMutation();
  const [updateBankDetails] = useUpdateBankDetailsMutation();
  const [updateMerchantProfile] = useUpdateMerchantProfileMutation();
  const [confirmPricingCommercials] = useConfirmPricingCommercialsMutation();
  const [saveRateCard, { isLoading: savingRateCard }] = useSaveRateCardMutation();
  const [saveTnc, { isLoading: savingTnc }] = useSaveTncMutation();
  const [commitPricingSchedule] = useCommitPricingScheduleMutation();
  const [executeCommit, { isLoading: executingCommit }] = useExecuteCommitMutation();
  const [submitKyc, { isLoading: kycSubmitting }] = useSubmitKycMutation();
  const [kycFileMap, setKycFileMap] = useState({});
  const [saveDraft] = useSaveDraftMutation();
  const [loadMerchantPricing] = useLazyLoadMerchantPricingQuery();

  // Keep wizard.serviceabilityGroups in sync with the global groups cache so
  // buildTemplateModel / rateEngine helpers can resolve parent couriers.
  const { data: svcGroups = [] } = useGetServiceabilityGroupsQuery();
  useEffect(() => {
    if (svcGroups.length) dispatch(setServiceabilityGroups(svcGroups));
  }, [svcGroups, dispatch]);

  // Fire-and-forget draft save — advances the stage immediately, saves in background.
  // startTransition marks the stage re-render as interruptible so React yields to the
  // browser between chunks, preventing the large Stage 4 table unmount from freezing the UI.
  const saveAndAdvance = (stageNum) => {
    if (wizard.merchantId) saveDraft(buildStageDraft(stageNum, wizard));
    startTransition(() => { dispatch(nextStage()); });
  };

  // Update mode: Sign-up + KYC are done — jump to Profile (Stage 2) and load existing pricing.
  useEffect(() => {
    if (mode === 'update') {
      dispatch(setEditMode(true));
      if (stage <= 1) dispatch(setStage(2));
      if (merchantId) loadMerchantPricing(merchantId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Hydrate merchant + form from the API. Used on mount (when ?merchantId is in
  // the URL after a refresh / deep link) and by the in-wizard merchant search.
  const hydrateMerchant = (urlMerchantId) => {
    if (!urlMerchantId) return;
    {
      // Restore locally-saved wizard state first (s3Key, filename, stage progress, etc.)
      // The API hydration below will override merchant/profile fields but preserves these.
      const saved = loadWizardState(urlMerchantId);
      if (saved) {
        if (saved.agreement?.s3Key || saved.agreement?.s3Url) {
          dispatch(setAgreement({
            s3Key: saved.agreement.s3Key || null,
            s3Url: saved.agreement.s3Url || null,
            filename: saved.agreement.filename || '',
          }));
        }
        if (saved.credit?.approval_s3Key) dispatch(updateCredit({ approval_s3Key: saved.credit.approval_s3Key }));
        // Restore the per-service account-code selection so a reload keeps only the
        // codes the analyst picked — instead of reverting to the all-selected default.
        if (saved.accountCodeSelection && typeof saved.accountCodeSelection === 'object') {
          Object.entries(saved.accountCodeSelection).forEach(([serviceId, codes]) => {
            if (Array.isArray(codes)) dispatch(setServiceAccountCodes({ serviceId, codes }));
          });
        }
        // Restore the Wallet-Share-to-Prozo slider — the API doesn't reliably
        // round-trip it, so without this it resets to the default on reload.
        if (saved.masterInfo?.prozo_wallet_share_pct != null) {
          dispatch(updateMasterInfo({ prozo_wallet_share_pct: saved.masterInfo.prozo_wallet_share_pct }));
        }
      }

      setFetchingMerchant(true);
      getMerchantById({ id: urlMerchantId }).then((result) => {
        const m = result?.data?.data || result?.data;
        if (!m || (!m.id && !m._id)) { setFetchingMerchant(false); return; }

        // Set merchant identity + details for read-only display
        dispatch(setMerchantId(m.id || m._id));
        dispatch(setMerchant({
          id: m.id || m._id,
          code: String(m.merchantId || ''),
          brand: m.name,
          legal: m.companyName || m.name,
          email: m.email,
          contact_no: m.contact_no,
          address: m.address,
          city: m.city,
          state: m.state,
          pincode: String(m.pincode || ''),
          status: m.status,
        }));
        // Populate signup state so the read-only card shows details
        dispatch(updateSignup({
          business_name: m.companyName || m.name || '',
          brand: m.name || '',
          email: m.email || '',
          contact: m.contact_no || '',
          address: m.address || '',
          pincode: String(m.pincode || ''),
          city: m.city || '',
          state: m.state || '',
          hubspotDealId: m.hubspotDealId || '',
          salesPocName: m.salesPocName || '',
        }));

        // Populate masterInfo — Source of Orders, AOV, volume, units, category, integration stack, wallet share
        dispatch(updateMasterInfo({
          ...(m.orderSource?.length ? { order_sources: m.orderSource } : {}),
          ...(m.merchantCategory ? { category: m.merchantCategory } : {}),
          ...(m.aov ? { aov: m.aov } : {}),
          ...(m.unitsPerOrder ? { units_per_order: m.unitsPerOrder } : {}),
          ...(m.monthlyShipmentVolume ? { monthly_volume: m.monthlyShipmentVolume } : {}),
          ...(m.integrationType ? { integration_type: m.integrationType } : {}),
          ...(m.storefrontPlatform ? { storefront_platform: m.storefrontPlatform } : {}),
          ...(m.omsPlatforms?.length ? { oms_platforms: m.omsPlatforms } : {}),
          ...(m.customOms?.length ? { custom_oms: m.customOms } : {}),
          ...(m.proZoWalletSharePct != null ? { prozo_wallet_share_pct: m.proZoWalletSharePct } : {}),
        }));
        // Populate Stage 3 — Profile & Service (freight directions, payment, services, warehouse)
        const DIR_MAP = { FORWARD: 'forward', REVERSE: 'reverse_noqc', RVP: 'reverse_qc' };
        if (Array.isArray(m.freightDirections) && m.freightDirections.length) {
          // Set exactly rather than toggling: a duplicate entry, or hydrating twice,
          // used to cancel itself out and leave the merchant with no directions at all.
          const wanted = new Set(m.freightDirections.map((d) => DIR_MAP[d]).filter(Boolean));
          Object.values(DIR_MAP).forEach((key) => {
            dispatch(setDirectionEnabled({ direction: key, value: wanted.has(key) }));
          });
        }
        if (m.paymentMode) dispatch(setPayment(m.paymentMode));
        if (Array.isArray(m.resolvedServiceTypes) && m.resolvedServiceTypes.length) {
          // Only set the mode checkboxes — the profileKey effect in Stage1_Profile
          // will derive the service types automatically. This keeps the bidirectional
          // mode ↔ service sync working correctly in both create and hydration mode.
          const serviceIds = new Set(m.resolvedServiceTypes);
          MODE_ROWS.forEach(({ k: mode }) => {
            ['reg', 'large'].forEach((size) => {
              const derived = derivedServicesForMode(mode, size === 'reg', size === 'large');
              // Set exactly, never toggle — hydrating twice used to flip these back off.
              dispatch(setModeEnabled({ mode, size, value: derived.some((id) => serviceIds.has(id)) }));
            });
          });
          // Seed resolvedServices too. It is otherwise ONLY produced by Stage 1's mount
          // effect, so any session that reaches a later stage without opening Profile
          // (deep link, refresh, resuming someone else's onboarding) carried an empty
          // list — and Stage 5 then refused to build the workbook, reporting "no services
          // selected" for a merchant that has five. Stage 1 re-derives from the profile
          // and overwrites this the moment it mounts, so it stays the source of truth.
          dispatch(setResolvedServices(m.resolvedServiceTypes.map((id) => {
            const meta = SERVICE_TYPES.find((sv) => sv.id === id) || { id, family: 'Other', sub: '' };
            return { ...meta, included: true, derivation: 'Restored from saved profile' };
          })));
        }
        const WH_MAP = { PROZO: 'yes', NON_PROZO: 'no', MIXED: 'mixed' };
        if (m.warehouseMode || m.selectedWarehouseCodes?.length) {
          dispatch(updateWarehouse({
            is_prozo: WH_MAP[m.warehouseMode] || 'yes',
            prozo_warehouses: m.selectedWarehouseCodes || [],
          }));
        }

        // Populate KYC text fields from API response
        const KYC_DOC_NAME_MAP = {
          BANK_DETAILS: 'bank', GST: 'gstin', PAN: 'pan',
          MSME_CERTIFICATE: 'msme', AADHAR_CARD: 'signatory', AADHAR: 'signatory',
        };
        if (m.pan)    dispatch(updateKycDoc({ docId: 'pan',      patch: { number: m.pan, status: 'uploaded' } }));
        if (m.gst)    dispatch(updateKycDoc({ docId: 'gstin',    patch: { number: m.gst, status: 'uploaded' } }));
        if (m.aadhar) dispatch(updateKycDoc({ docId: 'signatory', patch: { aadhaar_no: m.aadhar, status: 'uploaded' } }));
        if (m.msmeNotRegistered) {
          dispatch(updateKycDoc({ docId: 'msme', patch: { not_registered: true, status: 'not_required' } }));
        } else if (m.msmeNumber) {
          dispatch(updateKycDoc({ docId: 'msme', patch: { number: m.msmeNumber, status: 'uploaded' } }));
        }
        if (m.companyType) dispatch(setKycCompanyType(m.companyType.replace(/_/g, ' ')));
        if (m.bank_details) {
          dispatch(updateKycDoc({ docId: 'bank', patch: {
            bank_name: m.bank_details.bankName || '',
            account_no: m.bank_details.accountNumber || '',
            ifsc: m.bank_details.ifscCode || '',
            beneficiary_name: m.bank_details.beneficiaryName || '',
            account_type: m.bank_details.accountType || 'CURRENT',
            status: 'uploaded',
          }}));
        }
        // Populate kycDetails — presigned URLs for existing uploaded documents
        if (Array.isArray(m.kycDetails)) {
          m.kycDetails.forEach((kd) => {
            const docId = KYC_DOC_NAME_MAP[kd.kycDocName];
            if (!docId) return;
            dispatch(updateKycDoc({ docId, patch: {
              docUrl: kd.mongoSignedUrlDetails?.presignedUrl || kd.docUrl || '',
              uploadDate: kd.uploadDate || '',
              status: kd.verified ? 'approved' : kd.rejected ? 'rejected' : 'uploaded',
            }}));
          });
        }

        // Hydrate Stage 3 commercial construct fields
        if (m.gstStance || m.volumetricCoefficient || m.weightCeiling || m.commercialConstructConfirmed != null || m.agreementS3Url) {
          dispatch(setAgreement({
            ...(m.gstStance             && { gst_stance: m.gstStance.toLowerCase() }),
            ...(m.volumetricCoefficient && { vc: m.volumetricCoefficient }),
            ...(m.weightCeiling         && { weight_ceiling: m.weightCeiling }),
            ...(m.commercialConstructConfirmed != null && { confirmed: !!m.commercialConstructConfirmed }),
            // Restore agreement PDF from server URL — enables preview without local base64
            ...(m.agreementS3Url && { s3Url: m.agreementS3Url }),
          }));
        }
        if (m.rvpEnabled != null)  dispatch(setRvpEnabled(!!m.rvpEnabled));
        if (m.rvpMode)             dispatch(setRvpMode(m.rvpMode === 'FORMULA' ? 'forward_linked' : 'independent'));

        // Mode A reverse-rate terms. Without this the formula reverted to a
        // hard-coded { flat: 25, mult: 1.3 } on every reload, so the reverse rate
        // was derived from numbers nobody agreed to.
        if (m.rvpFormulaType != null || m.rvpFlatCharge != null
            || m.rvpMultiplier != null || m.rvpQcCharge != null) {
          dispatch(updateRvpFormula({ parent: '__blended__', patch: {
            ...(m.rvpFormulaType != null && { type: m.rvpFormulaType }),
            ...(m.rvpFlatCharge != null && { flat: Number(m.rvpFlatCharge) }),
            ...(m.rvpMultiplier != null && { mult: Number(m.rvpMultiplier) }),
            ...(m.rvpQcCharge != null && { qc: Number(m.rvpQcCharge) }),
          } }));
        }

        // Blended vs courier-wise. Not restoring this meant a blended merchant came
        // back as courier-wise, and Stage 3 then wrote that back over the saved value.
        // The backend stores it upper-cased ("BLENDED" / "COURIER_WISE") while the
        // slice compares against 'blended', so map rather than pass through.
        if (m.rateCardMode) {
          dispatch(setRateCardMode(m.rateCardMode.toUpperCase() === 'BLENDED' ? 'blended' : 'courier_wise'));
        }

        // Per-service arrangement. Same overwrite risk: Stage 3 re-sends an entry for
        // every resolved service, so an unrestored map was replaced by defaults.
        if (m.serviceArrangements && typeof m.serviceArrangements === 'object') {
          Object.entries(m.serviceArrangements).forEach(([serviceId, cfg]) => {
            if (!cfg) return;
            dispatch(setArrangement({ serviceId, patch: {
              ...(cfg.type && { type: cfg.type }),
              ...(cfg.mode && { mode: cfg.mode }),
              ...(cfg.ceiling != null && { ceiling: cfg.ceiling }),
            } }));
          });
        }

        // Engagement: the backend stores POSTPAID + isSecuredPostpaid, so reading
        // engagementModel alone collapsed "secured postpaid" down to "postpaid".
        if (m.engagementModel) {
          const eng = m.isSecuredPostpaid ? 'secured_postpaid' : m.engagementModel.toLowerCase();
          dispatch(updateCredit({ engagement: eng }));
        }

        // Credit terms. These have non-null defaults in the slice (0 / 30 / ''), so an
        // unrestored value was actively overwritten with the default on the next save.
        dispatch(updateCredit({
          ...(m.creditExposure != null && { credit_exposure: m.creditExposure }),
          ...(m.creditPeriodDays != null && { credit_period_days: m.creditPeriodDays }),
          ...(m.recommendedCreditLimit != null && { recommended_limit: m.recommendedCreditLimit }),
          ...(m.salesCreditOverride != null && { sales_override: m.salesCreditOverride }),
          ...(m.prepaidSplitPercent != null && { prepaid_split_pct: m.prepaidSplitPercent }),
          ...(m.avgInvoiceValue != null && { avg_invoice_value: m.avgInvoiceValue }),
          ...(m.creditApprovalS3Key && { approval_s3Key: m.creditApprovalS3Key }),
        }));

        if (m.codSplitPercent != null) dispatch(setCodSplit(m.codSplitPercent));
        if (m.codTiersWeight)  dispatch(updateBlendedTc({ cod_tiers_weight: m.codTiersWeight }));
        if (m.codTiersInvoice) dispatch(updateBlendedTc({ cod_tiers_invoice: m.codTiersInvoice }));
        if (m.pricingEffectiveMode) dispatch(setEffectiveMode(m.pricingEffectiveMode.toLowerCase()));
        if (m.invoiceCeiling)      dispatch(setInvoiceCeiling(m.invoiceCeiling));
        if (m.codStructure)        dispatch(updateBlendedTc({ cod_structure: m.codStructure.toLowerCase() }));
        if (m.globalRto?.mode) {
          dispatch(updateRto({ mode: m.globalRto.mode.toLowerCase() }));
          if (m.globalRto.pct_fwd != null) dispatch(updateRto({ pct_fwd: m.globalRto.pct_fwd }));
        }

        // Hide skeleton only after all data is in Redux
        setFetchingMerchant(false);

        // Populate existing pickup addresses in WarehouseCard (read-only display)
        const pickups = Array.isArray(m.pickup_details) ? m.pickup_details.filter((p) => p.status === 'ACTIVE') : [];
        if (pickups.length > 0) {
          const WH_MODE_MAP = { PROZO: 'yes', NON_PROZO: 'no', MIXED: 'mixed' };
          dispatch(updateWarehouse({
            // Respect warehouseMode from API; fall back to 'no' only when no warehouseMode
            is_prozo: WH_MODE_MAP[m.warehouseMode] || 'no',
            non_prozo: pickups.map((p) => ({
              id: p.id || ('existing-' + p.pickupProzoId || Date.now()),
              label: p.name || p.city,
              contact_name: p.name || '',
              contact_number: p.contactNumber || '',
              gstin: p.gstin || '',
              line1: p.address_line || '',
              line2: '',
              city: p.city || '',
              state: p.state || '',
              pincode: String(p.pincode || ''),
              _registered: true, // already exists on server — skip bulk re-registration
            })),
          }));
        }
      });
    }
  };

  // On mount: if ?merchantId is in the URL (refresh / deep link), hydrate.
  useEffect(() => {
    const urlMerchantId = new URLSearchParams(window.location.search).get('merchantId');
    if (urlMerchantId && !wizard.merchantId) hydrateMerchant(urlMerchantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // In-wizard merchant search → load the selected merchant into the flow.
  const handleSearchSelect = (id) => {
    if (!id || id === wizard.merchantId) return;
    window.history.replaceState({}, '', `${window.location.pathname}?merchantId=${id}`);
    hydrateMerchant(id);
  };

  // Scroll to top + auto-save draft on every stage transition.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (merchantId) saveWizardState(merchantId, wizard);
    // Ensure rate-card rows exist when landing on Rate Card / Review / Commit
    // (e.g. when jumping via the stepper without passing through Stage 4 first).
    if (stage >= 4 && wizard.rateCard.rows.length === 0 && resolved.length > 0) {
      dispatch(setRateCardRows(buildRateCardRows(wizard)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // Persist the per-service account-code selection as soon as it changes, so a
  // reload restores exactly what the analyst picked (not just on stage change).
  useEffect(() => {
    if (merchantId) saveWizardState(merchantId, wizard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard.accountCodeSelection]);

  const handleNext = async () => {
    if (stage === 0) {
      const signupErrs = wizard.merchantId ? {} : validateSignup(signup);
      setSignupErrors(signupErrs);
      if (Object.keys(signupErrs).length > 0) {
        enqueueSnackbar('Please fix the highlighted fields', { variant: 'error' });
        return;
      }
      let resolvedMerchantId = wizard.merchantId;
      if (!resolvedMerchantId) {
        try {
          const merchant = await createMerchant(signup).unwrap();
          resolvedMerchantId = merchant.id || merchant._id;
          window.history.replaceState({}, '', `${window.location.pathname}?merchantId=${resolvedMerchantId}`);
          // Persist masterInfo fields not included in createMerchant (category, integration stack, wallet share)
          try { await updateMasterInfoOnly().unwrap(); } catch { /* non-blocking */ }
          enqueueSnackbar('Merchant created · moving to KYC', { variant: 'success' });
        } catch (err) {
          enqueueSnackbar(err?.data?.message || err?.message || 'Failed to create merchant — please retry', { variant: 'error' });
          return;
        }
      } else {
        // Merchant already exists — save all masterInfo changes before advancing
        try {
          await updateMasterInfoOnly().unwrap();
        } catch (err) {
          enqueueSnackbar(err?.data?.message || err?.message || 'Failed to save merchant info — please retry', { variant: 'error' });
          return;
        }
      }
      startTransition(() => { dispatch(nextStage()); });
      return;
    }
    if (stage === 1) {
      const kyc = wizard.kyc;
      const hasAnyFile = Object.values(kycFileMap).some(Boolean);
      // Only call the KYC API if the user actually modified something this session
      // (kycUserModified is false after hydration — only set to true by user interactions)
      if (!kyc.submittedAt && (hasAnyFile || wizard.kycUserModified)) {
        try {
          await submitKyc({ files: kycFileMap, companyType: kyc.companyType }).unwrap();
          dispatch(setKycUserModified(false)); // reset after successful submission
          enqueueSnackbar('KYC documents submitted successfully', { variant: 'success' });
        } catch (err) {
          enqueueSnackbar(err?.data?.message || err?.message || 'KYC submission failed — please retry', { variant: 'error' });
          return;
        }
      }
      // Stage 1c: save bank details only if user actually edited them this session
      if (wizard.kycUserModified && kyc.docs.bank.account_no && kyc.docs.bank.ifsc) {
        updateBankDetails(); // fire and forget
      }
      saveAndAdvance(1);
      return;
    }
    if (stage === 2) {
      const hasDirection = Object.values(profile.directions).some(Boolean);
      if (!hasDirection) { enqueueSnackbar('Select at least one freight direction', { variant: 'warning' }); return; }
      if (!profile.payment) { enqueueSnackbar('Select a payment mode', { variant: 'warning' }); return; }
      if (resolved.length === 0) { enqueueSnackbar('Select at least one service type', { variant: 'warning' }); return; }

      console.log('[Step 3] Form data on Next:', {
        freightDirections: wizard.profile.directions,
        paymentMode: wizard.profile.payment,
        modesOfDelivery: wizard.profile.modes,
        resolvedServices: wizard.resolvedServices,
        warehouse: { type: wizard.warehouse.is_prozo, prozoWarehouses: wizard.warehouse.prozo_warehouses, nonProzoWarehouses: wizard.warehouse.non_prozo },
      });

      // 2a: update merchant profile
      try {
        await updateMerchantProfile().unwrap();
      } catch (err) {
        enqueueSnackbar(err?.data?.message || err?.message || 'Failed to save profile — please retry', { variant: 'error' });
        return;
      }

      // 2b + 2c: register only NEW non-Prozo warehouses (not already sent to the API).
      // Warehouses saved via the dialog already have _registered: true — skip them.
      wizard.warehouse.non_prozo.forEach((wh, idx) => {
        if (wh._registered) return; // already registered in the dialog flow
        if (wh.line1 && wh.city && wh.state) {
          registerPickup({
            merchantId: wizard.merchantId,
            status: 'ACTIVE',
            address_type: 'PICKUP',
            country: 'India',
            shipmentType: 'B2B',
            name: wh.contact_name || wh.label || wh.city,
            address_line: [wh.line1, wh.line2].filter(Boolean).join(', '),
            city: wh.city,
            state: wh.state,
            pincode: wh.pincode,
            contactNumber: wh.contact_number || '',
            gstin: wh.gstin || '',
            is_default: idx === 0,
          });
        }
      });

      saveAndAdvance(2);
      return;
    }
    if (stage === 3) {
      const errs = validateAgreement(agreement, wizard.rvp, wizard.customSlabParents);
      if (Object.keys(errs).length > 0) {
        enqueueSnackbar(Object.values(errs)[0], { variant: 'warning' });
        return;
      }
      try {
        await confirmPricingCommercials().unwrap();
        enqueueSnackbar('Commercial construct confirmed', { variant: 'success' });
      } catch (err) {
        enqueueSnackbar(err?.data?.message || err?.message || 'Failed to save commercials — please retry', { variant: 'error' });
        return;
      }
      saveAndAdvance(3);
      return;
    }
    if (stage === 4) {
      if (wizard.rateCard.rows.length === 0) {
        enqueueSnackbar('Upload the filled rate-card workbook before continuing.', { variant: 'warning' });
        return;
      }
      const errs = validateRateCard(wizard);
      if (Object.keys(errs).length > 0) {
        enqueueSnackbar(Object.values(errs)[0], { variant: 'warning' });
        return;
      }
      try {
        await saveRateCard().unwrap();
        enqueueSnackbar('Rate card saved', { variant: 'success' });
      } catch (err) {
        enqueueSnackbar(err?.data?.message || err?.message || 'Failed to save rate card — please retry', { variant: 'error' });
        return;
      }
      saveAndAdvance(4);
      return;
    }
    if (stage === 5) {
      const errs = validateTcs(wizard);
      if (Object.keys(errs).length > 0) {
        enqueueSnackbar(Object.values(errs)[0], { variant: 'warning' });
        return;
      }
      try {
        await saveTnc().unwrap();
        enqueueSnackbar('T&Cs saved', { variant: 'success' });
      } catch (err) {
        enqueueSnackbar(err?.data?.message || err?.message || 'Failed to save T&Cs — please retry', { variant: 'error' });
        return;
      }
      dispatch(recordConfirmation({ key: 'tcs_blended', by: currentUser }));
      saveAndAdvance(5);
      return;
    }
    if (stage === 6) {
      const errs = validateReview(wizard);
      if (Object.keys(errs).length > 0) {
        enqueueSnackbar(Object.values(errs)[0], { variant: 'warning' });
        return;
      }
      dispatch(recordConfirmation({ key: 'consolidated_review', by: currentUser }));
      try {
        await commitPricingSchedule().unwrap();
        enqueueSnackbar('Review confirmed and schedule saved', { variant: 'success' });
      } catch (err) {
        enqueueSnackbar(err?.data?.message || err?.message || 'Failed to save review — please retry', { variant: 'error' });
        return;
      }
      saveAndAdvance(6);
      return;
    }
    startTransition(() => { dispatch(nextStage()); });
  };

  const handleBack = () => dispatch(prevStage());

  const renderStage = () => {
    switch (stage) {
      case 0:
        return <Stage0_Signup errors={signupErrors} />;
      case 1:
        return <Stage1_Kyc onFileCapture={(docId, f) => setKycFileMap((prev) => ({ ...prev, [docId]: f }))} />;
      case 2:
        return <Stage2_Profile />;
      case 3:
        return <Stage3_Commercials />;
      case 4:
        return <Stage4_RateCard />;
      case 5:
        return <Stage5_Tcs />;
      case 6:
        return <Stage6_Review />;
      case 7:
        return <Stage7_Commit onCommitted={() => startTransition(() => { dispatch(nextStage()); })} />;
      case 8:
        return <Stage8_Done />;
      default:
        return null;
    }
  };

  const nextLabel = (() => {
    if (stage === 0) return mode === 'update' ? 'Save & Continue' : 'Create Merchant & Continue';
    if (stage === 1) return 'Continue (KYC runs in parallel)';
    if (stage === 3) return 'Confirm and continue';
    if (stage === 4) return 'Confirm and continue';
    if (stage === 5) return 'Confirm and continue';
    if (stage === 6) return 'Validate & lock for commit';
    if (stage === 7) return null; // commit handled inside the stage
    if (stage === STAGES.length - 1) return null;
    return `Next: ${STAGES[stage + 1]?.short || ''}`;
  })();

  if (fetchingMerchant) {
    return (
      <Box>
        {/* stepper skeleton */}
        <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {Array.from({ length: 9 }).map((_, i) => (
              <Stack key={i} direction="row" alignItems="center" sx={{ flex: 1 }}>
                {i > 0 && <Skeleton variant="rectangular" height={2} sx={{ flex: 1, mr: 1 }} />}
                <Skeleton variant="rounded" width={80} height={28} sx={{ borderRadius: '20px', flexShrink: 0 }} />
              </Stack>
            ))}
          </Stack>
        </Box>

        {/* content skeleton */}
        <Box sx={{ maxWidth: 1640, mx: 'auto', px: 0, pt: 2.25, pb: 4 }}>
          {/* stage header */}
          <Stack spacing={0.75} sx={{ mb: 1.75 }}>
            <Skeleton variant="text" width={220} height={28} />
            <Skeleton variant="text" width={360} height={18} />
          </Stack>

          {/* main content card */}
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, py: 2.75, px: 0 }}>
            <Stack spacing={2.5}>
              {/* section title */}
              <Skeleton variant="text" width={160} height={22} />

              {/* merchant info card */}
              <Skeleton variant="rounded" height={88} sx={{ borderRadius: 2.5 }} />

              {/* form rows */}
              <Stack direction="row" spacing={2}>
                <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
                <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
                <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
              </Stack>
              <Skeleton variant="rounded" height={40} />
              <Stack direction="row" spacing={2}>
                <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
                <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
                <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
              </Stack>

              {/* master info section */}
              <Skeleton variant="text" width={140} height={18} sx={{ mt: 1 }} />
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} variant="rounded" width={90} height={28} sx={{ borderRadius: '8px' }} />
                ))}
              </Stack>
              <Stack direction="row" spacing={2}>
                <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
                <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
                <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
              </Stack>

              {/* pickup address card */}
              <Skeleton variant="text" width={180} height={18} sx={{ mt: 1 }} />
              <Skeleton variant="rounded" height={100} sx={{ borderRadius: 2.5 }} />
            </Stack>
          </Box>

          {/* footer */}
          <Box sx={{ mt: 1.75, border: 1, borderColor: 'divider', borderRadius: 3, px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Skeleton variant="rounded" width={160} height={40} sx={{ borderRadius: '12px' }} />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <PageTitle>
        {mode === 'update' ? 'Update Pricing Setup' : 'Merchant Pricing Setup'}
      </PageTitle>
      <Box>
        <Box sx={{ px: 0, pt: 2 }}>
          {mode === 'update' && (
            <Box sx={{ mb: 1.5 }}>
              <Chip
                size="small"
                label="Update Mode · merchant already exists, Signup step skipped"
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          )}
        </Box>
        <WizardTopBar onOpen={setDrawerKind} />
        <WizardStepper />
        <Box
          sx={{
            maxWidth: 1640,
            mx: 'auto',
            px: 0,
            pt: 2.25,
            pb: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.75,
          }}
        >
          {stage === 0 && mode !== 'update' && !wizard.merchantId && (
            <MerchantSearchBar onSelect={handleSearchSelect} />
          )}
          <StageHeader stage={stage} />
          <Box
            sx={{
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              boxShadow: 'none',
              p: 2.75,
            }}
          >
            {renderStage()}
          </Box>
          <StageFooter
            stage={stage}
            totalStages={STAGES.length}
            onBack={handleBack}
            onNext={handleNext}
            nextLabel={
              creatingMerchant ? 'Creating merchant…' :
              kycSubmitting ? 'Submitting KYC…' :
              savingRateCard ? 'Saving rate card…' :
              savingTnc ? 'Saving T&Cs…' :
              isPending ? 'Loading…' :
              nextLabel
            }
            hideNext={stage === 7}
            nextDisabled={
              creatingMerchant || kycSubmitting || savingRateCard || savingTnc || isPending ||
              // Stage 1 — if any KYC data entered, mandatory fields must be complete
              (stage === 1 && !wizard.kyc.submittedAt &&
                hasAnyKycData(wizard.kyc, kycFileMap) &&
                !mandatoryKycComplete(wizard.kyc, kycFileMap)) ||
              // Stage 2 — block until freight direction + payment + service type are all set
              (stage === 2 && (
                !Object.values(profile.directions).some(Boolean) ||
                !profile.payment ||
                resolved.length === 0
              ))
            }
            draftedAt={wizard.lastSavedAt}
          />
        </Box>
        <WizardDrawer open={Boolean(drawerKind)} kind={drawerKind} onClose={() => setDrawerKind(null)} />
      </Box>
    </>
  );
}
